import React, { useMemo, useState } from 'react';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import * as Lucide from 'lucide-react';
import dayjs from 'dayjs';
import useAlturaTeclado from '../hooks/useAlturaTeclado';

// Espelha o limite do servidor (AmostraService.COMENTARIO_MAX_CHARS). Barrar aqui e so
// cortesia — quem manda e o backend, que devolve 400.
const MAX_CHARS = 1000;

/**
 * Histórico + novo comentário de uma Análise, num diálogo só.
 *
 * No card de Amostras isso são dois modais separados ("Histórico" e "Adicionar
 * Comentário"), o que obriga o usuário a fechar um para abrir o outro. Aqui os dois
 * ficam juntos: quem vai comentar quase sempre quer antes ver o que já foi dito.
 *
 * Os comentários são append-only (o servidor não expõe editar nem excluir), então não há
 * ação por item — é um registro, não uma lista editável.
 */
export default function ComentariosAnaliseDialog({
    open,
    onClose,
    identificacao,
    comentarios,
    onAdicionar,
    somenteLeitura = false,
}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const alturaTeclado = useAlturaTeclado();

    const [texto, setTexto] = useState('');
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState('');

    // Mais recente no topo. `createdAt` vem do servidor em ISO com offset BR, mas um
    // registro torto (ou de uma versao antiga) nao pode derrubar o modal inteiro —
    // por isso o fallback em vez de confiar no parse.
    const ordenados = useMemo(() => {
        const lista = Array.isArray(comentarios) ? comentarios : [];
        return [...lista].sort((a, b) => {
            const da = dayjs(a?.createdAt);
            const db = dayjs(b?.createdAt);
            if (!da.isValid() && !db.isValid()) return 0;
            if (!da.isValid()) return 1;
            if (!db.isValid()) return -1;
            return db.valueOf() - da.valueOf();
        });
    }, [comentarios]);

    const podeSalvar = texto.trim().length > 0 && !salvando;

    const fechar = () => {
        if (salvando) return;
        setTexto('');
        setErro('');
        onClose?.();
    };

    const salvar = async () => {
        if (!podeSalvar) return;
        setSalvando(true);
        setErro('');
        try {
            await onAdicionar?.(texto.trim());
            // O pai atualiza a lista via prop; aqui so limpamos o campo.
            setTexto('');
        } catch (e) {
            setErro(e?.message || 'Falha ao adicionar comentário.');
        } finally {
            setSalvando(false);
        }
    };

    const formatarData = (valor) => {
        const d = dayjs(valor);
        return d.isValid() ? d.format('DD/MM/YYYY HH:mm') : '—';
    };

    return (
        <Dialog
            open={!!open}
            onClose={fechar}
            fullWidth
            maxWidth="sm"
            scroll="paper"
            // O teclado do celular cobre a parte de baixo da tela, e o Dialog e
            // posicionado pela viewport de LAYOUT — que nao encolhe. Sem isto, o campo
            // de texto empurra "Comentar" e "Fechar" para tras do teclado.
            //
            // Encolher o CONTAINER resolve as duas coisas de uma vez: ele centraliza o
            // dialogo na area que sobrou (o dialogo sobe) e o maxHeight do Paper, que e
            // relativo ao container, acompanha (o dialogo encurta em vez de vazar).
            sx={{
                '& .MuiDialog-container': {
                    height: alturaTeclado
                        ? `calc(100% - ${alturaTeclado}px)`
                        : '100%',
                    transition: 'height 0.2s ease-out',
                },
            }}
            PaperProps={{
                sx: {
                    borderRadius: isMobile ? 2 : 3,
                    m: isMobile ? 1 : 4,
                    width: isMobile ? 'calc(100% - 16px)' : undefined,
                    maxHeight: isMobile ? 'calc(100% - 32px)' : undefined,
                },
            }}
        >
            <DialogTitle
                sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 2 }}
            >
                <Lucide.MessageSquare
                    size={20}
                    color={theme.palette.action.active}
                    aria-hidden="true"
                />
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
                        Comentários
                    </Typography>
                    {identificacao && (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            Análise {identificacao}
                        </Typography>
                    )}
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                {ordenados.length === 0 ? (
                    <Stack alignItems="center" spacing={1} sx={{ py: 3 }}>
                        <Lucide.MessageSquareDashed
                            size={32}
                            color={theme.palette.text.disabled}
                            aria-hidden="true"
                        />
                        <Typography variant="body2" color="text.secondary">
                            Nenhum comentário ainda.
                        </Typography>
                        {!somenteLeitura && (
                            <Typography
                                variant="caption"
                                color="text.disabled"
                                textAlign="center"
                            >
                                Registre aqui o que aconteceu no ensaio — chuva na
                                captação, efluente turvo, coleta atrasada.
                            </Typography>
                        )}
                    </Stack>
                ) : (
                    <Stack divider={null}>
                        {ordenados.map((c, i) => (
                            <Box
                                key={`${c?.createdAt || 'sem-data'}-${i}`}
                                sx={{
                                    py: 1.5,
                                    borderBottom:
                                        i < ordenados.length - 1
                                            ? '1px solid'
                                            : 'none',
                                    borderColor: 'divider',
                                }}
                            >
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                    mb={0.5}
                                >
                                    <Avatar
                                        sx={{
                                            width: 24,
                                            height: 24,
                                            fontSize: '0.7rem',
                                        }}
                                    >
                                        {String(c?.author || '?')
                                            .charAt(0)
                                            .toUpperCase()}
                                    </Avatar>
                                    <Typography
                                        variant="subtitle2"
                                        sx={{ minWidth: 0 }}
                                    >
                                        {c?.author || 'Usuário'}{' '}
                                        <Typography
                                            component="span"
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            • {formatarData(c?.createdAt)}
                                        </Typography>
                                    </Typography>
                                </Stack>
                                <Typography
                                    variant="body2"
                                    sx={{ whiteSpace: 'pre-wrap', pl: 4 }}
                                >
                                    {c?.text}
                                </Typography>
                            </Box>
                        ))}
                    </Stack>
                )}

                {!somenteLeitura && (
                    <Box sx={{ mt: 2 }}>
                        {erro && (
                            <Alert
                                severity="error"
                                sx={{ mb: 1.5 }}
                                onClose={() => setErro('')}
                            >
                                {erro}
                            </Alert>
                        )}
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label="Novo comentário"
                            placeholder="O que aconteceu neste ensaio?"
                            value={texto}
                            onChange={(e) => setTexto(e.target.value)}
                            disabled={salvando}
                            inputProps={{ maxLength: MAX_CHARS }}
                            helperText={`${texto.length}/${MAX_CHARS}`}
                            FormHelperTextProps={{
                                sx: { textAlign: 'right', mx: 0 },
                            }}
                        />
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={fechar} disabled={salvando}>
                    Fechar
                </Button>
                {!somenteLeitura && (
                    <Button
                        onClick={salvar}
                        variant="contained"
                        disableElevation
                        disabled={!podeSalvar}
                        startIcon={
                            salvando ? null : <Lucide.Send size={16} />
                        }
                    >
                        {salvando ? 'Salvando…' : 'Comentar'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}
